/* eslint-disable @typescript-eslint/no-unsafe-return */

'use client';

import { useInfiniteQuery } from 'react-query';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';

import { useOAIQuery } from '~/hooks/reactQuery';
import { fetchOAIData } from '~/libs/openapi';
import type { OAISchema } from '~/types/openapi';

import { campaginDateRange } from './helper';

/**
 * 캠페인 리스트 조회
 * */
export function useCampaignList(date: string = dayjs().format('YYYY-MM-DD')) {
  return useOAIQuery({
    queryKey: '/api/market-place/v1/campaigns',
    queryOptions: {
      useErrorBoundary: true,
    },
    variables: {
      date,
    },
  });
}

/**
 * 캠페인 상세 조회
 * */
export function useCampaignDetail({
  campaignCode,
  preview,
}: {
  campaignCode: string;
  preview: boolean;
}) {
  return useOAIQuery({
    queryKey: '/api/market-place/v1/campaigns/{campaignCode}',
    variables: {
      campaignCode,
      preview,
    },
    queryOptions: {
      enabled: !!campaignCode,
      useErrorBoundary: true,
      suspense: false,
    },
  });
}

/**
 * 캠페인 상세 조회 (무한 스크롤)
 * */
export function useCampaignDetailInfinite({
  size,
  params,
  queryOptions,
}: {
  size?: number;
  params?: Record<string, unknown>;
  queryOptions?: Record<string, unknown>;
} = {}) {
  const router = useRouter();

  const variables = {
    ...router.query,
  };

  const infiniteQuery = useInfiniteQuery(
    ['/api/market-place/v1/campaigns/{campaignCode}', size, params, variables],
    async ({ signal, pageParam }) => {
      const data = await fetchOAIData<OAISchema['CampaignProductsResponse']>({
        queryKey: '/api/market-place/v1/campaigns/{campaignCode}',
        variables: {
          ...variables,
          ...((pageParam || {}) as Record<string, unknown>),
        },
        fetchOptions: {
          signal,
        },
      });

      return data;
    },
    {
      ...queryOptions,
      keepPreviousData: true,
      enabled: !!variables.campaignCode,
      getNextPageParam: (param) => {
        const { hasNextPage, sortType, searchAfter } = param.products as {
          content: OAISchema['MarketShowcaseProductsResponseV3'][];
          hasNextPage: boolean;
          size: number;
          sortType: string;
          searchAfter: string[];
        };

        if (!hasNextPage) {
          return undefined;
        }

        return { sortType, searchAfter };
      },
    },
  );

  const fetchNextPage = () => {
    if (infiniteQuery.isFetching || !infiniteQuery.hasNextPage) {
      return;
    }

    void infiniteQuery.fetchNextPage();
  };

  const { pages, count } = (() => {
    const list = infiniteQuery.data?.pages;

    return {
      pages: list?.map(({ products }) => products?.content).flat(),
      count: list?.[0]?.count || 0,
    };
  })();

  return {
    ...infiniteQuery,
    pages,
    count,
    fetchNextPage,
  };
}

export function useTodayCampaignProducts() {
  const { query } = useRouter();
  const { twoDepthId, vendorId } = query;

  const variables = {
    ...(twoDepthId && { displayCategoryIds: [Number(twoDepthId as string)] }),
    ...(vendorId && { vendorId: Number(vendorId as string) }),
  };

  return useOAIQuery({
    queryKey: '/api/market-place/v1/campaigns/products',
    queryOptions: {
      useErrorBoundary: true,
    },
    variables,
  });
}

/**
 * 캠페인 리스트 조회(홈)
 * */
export function useHomeCampaignList() {
  const router = useRouter();

  const variables = {
    date: (router.query.campaignDate || campaginDateRange.TODAY) as string,
  };

  return useOAIQuery({
    queryKey: '/api/market-place/v1/campaigns/home',
    variables,
    queryOptions: {
      useErrorBoundary: true,
    },
  });
}
